import React, { useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import EmailEditor, { EditorRef, EmailEditorProps } from "react-email-editor";

import { FormProvider, useForm } from "react-hook-form";
import { IDropdownOption, InputLabel, PaziaInput, PaziaSwitch } from "components/atoms";
import {
  EMAIL_TEMPLATE_TYPE_OPTIONS,
  EmailTemplateTypeValues,
  placeholdersOptions,
  systemEmailTemplateTypes,
} from "consts/emailTemplateType";
import { PaziaSelect } from "components/atoms";
import { CreateEmailTemplateRequest, EmailTemplateApi } from "api";
import { PaziaButton } from "components/atoms";
import { ClipboardText, PaziaBox, PaziaChip } from "components/atoms";
import { PaziaMultiSelect } from "components/atoms";
import { useCategoriesOptions } from "api/categories";
import { useThemeProvider } from "utils/ThemeContext";

interface EmailTemplateFormProps {
  onSubmit: (data: CreateEmailTemplateRequest) => void;
  template?: EmailTemplateApi;
  initialType?: number;
  isGeneric?: boolean;
}

export const EmailTemplateForm = ({ onSubmit, template, initialType, isGeneric }: EmailTemplateFormProps) => {
  const { loading, options } = useCategoriesOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: template?.id,
      subject: template?.subject,
      name: template?.name,
      isActive: template?.isActive ?? true,
      type: template?.type ?? initialType,
      delay: template?.delay === undefined ? 0 : template?.delay > 23 ? template.delay / 24 : template?.delay,
      delayType:
        template?.delay === undefined ? DELAY_DAY_TYPE : template.delay < 24 ? DELAY_HOUR_TYPE : DELAY_DAY_TYPE,
      [FORM_FIELDS.CATEGORIES]: template?.categories ?? [],
    },
  });
  const emailEditorRef = useRef<EditorRef>(null);
  const { currentTheme } = useThemeProvider();
  const { handleSubmit, watch, formState } = methods;
  const onReady: EmailEditorProps["onReady"] = (unlayer) => {
    if (template && template.jsonBody) unlayer.loadDesign(JSON.parse(template.jsonBody));
  };

  const onSubmitHandler = async (data: EmailTemplateValues) => {
    const request = {
      name: data.name,
      subject: data.subject,
      type: data.type ?? 0,
      isActive: data.isActive,
      delay: data.delayType === DELAY_DAY_TYPE ? data.delay * 24 : data.delay,
      htmlBody: "",
      jsonBody: "",
      categories: data.categories?.map((x) => Number(x)) ?? [],
    };
    const unlayer = emailEditorRef.current?.editor;
    //@ts-ignore
    unlayer?.exportHtml((data) => {
      const { design, html } = data;
      request.htmlBody = html ?? "";
      request.jsonBody = JSON.stringify(design) ?? "";
      onSubmit(request);
    });
  };

  const selectedType = watch(FORM_FIELDS.TYPE) as EmailTemplateTypeValues;

  // if selected type in systemEmailTemplateTypes then clear categories
  const onTypeChange = (opt: IDropdownOption) => {
    if (systemEmailTemplateTypes.includes(Number(opt.value))) {
      methods.setValue(FORM_FIELDS.CATEGORIES, []);
      methods.trigger(FORM_FIELDS.CATEGORIES);
    }
  };

  return (
    <PaziaBox className="mt-4">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitHandler)} className="mb-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex gap-2 sm:col-span-2">
            <div>
              <InputLabel label="Aktywny" name={FORM_FIELDS.IS_ACTIVE} />
              <PaziaSwitch name={FORM_FIELDS.IS_ACTIVE} className="mt-2" />
            </div>
            <PaziaInput required label="Nazwa szablonu" name={FORM_FIELDS.NAME} className="w-full" />
          </div>
          <div className="flex items-end justify-end">
            <PaziaButton title="Zapisz" type="success" />
          </div>
          {!isGeneric && (
            <PaziaMultiSelect
              options={options}
              isLoading={loading}
              name={FORM_FIELDS.CATEGORIES}
              disabled={systemEmailTemplateTypes.includes(selectedType)}
              required={!systemEmailTemplateTypes.includes(selectedType)}
              label="Kategorie"
              className="sm:col-span-2"
            />
          )}
          {!isGeneric && (
            <PaziaSelect
              required
              isClearable={false}
              options={EMAIL_TEMPLATE_TYPE_OPTIONS}
              name={FORM_FIELDS.TYPE}
              onChange={onTypeChange}
              label="Typ"
              disabled={!!initialType}
            />
          )}
          <PaziaInput type="number" label="Opóźnienie" name={FORM_FIELDS.DELAY} />
          <PaziaSelect
            isClearable={false}
            options={[
              { value: DELAY_HOUR_TYPE, label: "godziny" },
              { value: DELAY_DAY_TYPE, label: "dni" },
            ]}
            name={FORM_FIELDS.DELAY_TYPE}
            label="Typ opóźnienia"
          />
          <PaziaInput required label="Tytuł maila" name={FORM_FIELDS.SUBJECT} className="sm:col-span-3" />
        </form>
        <div className="mb-4">
          {placeholdersOptions[selectedType]?.map((placeholderGroup) => (
            <div key={placeholderGroup.name}>
              <div className="text-sm font-semibold">{placeholderGroup.name}</div>
              <div className="flex flex-wrap gap-1">
                {placeholderGroup.placeholders.map((placeholder) => (
                  <PaziaChip key={placeholder.value} tooltip={placeholder.tooltip}>
                    <ClipboardText className="mr-2" text={placeholder.value} />
                  </PaziaChip>
                ))}
              </div>
            </div>
          ))}
        </div>
      </FormProvider>
      <EmailEditor
        ref={emailEditorRef}
        onReady={onReady}
        style={{ height: "calc(100vh - 180px)" }}
        options={{ appearance: { theme: currentTheme === "dark" ? "modern_dark" : "modern_light" } }}
      />
    </PaziaBox>
  );
};

export const DELAY_HOUR_TYPE = 1;
export const DELAY_DAY_TYPE = 2;
const FORM_FIELDS = {
  ID: "id",
  NAME: "name",
  SUBJECT: "subject",
  TYPE: "type",
  DELAY: "delay",
  DELAY_TYPE: "delayType",
  IS_ACTIVE: "isActive",
  CATEGORIES: "categories",
} as const;
const schema = yup
  .object({
    [FORM_FIELDS.ID]: yup.number(),
    [FORM_FIELDS.NAME]: yup.string().required(),
    [FORM_FIELDS.SUBJECT]: yup.string().required(),
    [FORM_FIELDS.TYPE]: yup.number().notRequired(),
    [FORM_FIELDS.DELAY]: yup.number().required(),
    [FORM_FIELDS.IS_ACTIVE]: yup.boolean().required().default(true),
    [FORM_FIELDS.DELAY_TYPE]: yup.number().required(),
    [FORM_FIELDS.CATEGORIES]: yup.array().when(FORM_FIELDS.TYPE, {
      is: (value: number) => value && !systemEmailTemplateTypes.includes(value),
      then: (shema) => shema.min(1).required(),
      otherwise: (shema) => shema.notRequired(),
    }),
  })
  .required();

export type EmailTemplateValues = yup.InferType<typeof schema>;
